// Team › Fleet (`/c/{slug}/agents/fleet`; MONITORING-P2, docs/design/2026-11-01-monitoring-in-
// companyos.md §3): per DE seat, where its bridge runs (the host its heartbeat names, else the
// event source), the bridge version, the last heartbeat and the last event, telemetry on or off,
// the runs it has to do and its open alerts. From the bridges' own telemetry, not the satellite
// registry. A DE whose telemetry is on, with a run to do and quiet past the company's limit, is
// marked "Silent" (the `bridge_silent` alert). Managers only. Calls on load: `monitoring.fleet`
// (again every minute).
import {
  Badge,
  EmptyState,
  InfoTip,
  LocationHeader,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ServerIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { fleetQuery } from "./agent-queries";
import { LIST_BODY } from "./agents-ux";
import { ALERT_KIND_LABELS } from "./run-telemetry";

type Fleet = Awaited<ReturnType<Api["client"]["monitoring"]["fleet"]>>;
type Row = Fleet["data"][number];

const linkCls = "text-primary-text underline-offset-4 hover:underline";

/** Telemetry on, a run to do, nothing heard for longer than the limit. */
export function isSilent(r: Row, limitMinutes: number, now = Date.now()): boolean {
  if (r.telemetry_on === false || r.live_runs === 0 || r.last_event_at === null) return false;
  return now - new Date(r.last_event_at).getTime() > limitMinutes * 60_000;
}

function uptime(s: number): string {
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min`;
  if (s < 86_400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86_400)} d`;
}

export function FleetScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(fleetQuery(api, company.id));
  const rows = q.data?.data ?? [];
  const limit = q.data?.silent_after_minutes ?? 30;
  return (
    <div className={LIST_BODY} data-slot="fleet">
      <LocationHeader
        icon={ServerIcon}
        title="Fleet"
        badge={
          <InfoTip label="About the fleet">
            Where each digital employee's bridge runs, as the bridge itself reports it: its host,
            version, last heartbeat and last event. A DE with telemetry on and work to do that has
            said nothing for {limit} minutes raises a "Bridge silent" alert.
          </InfoTip>
        }
        actions={
          <span className="flex gap-3 text-sm">
            <Link
              to="/c/$slug/agents/alerts"
              params={{ slug: company.slug }}
              search={keepFocus}
              className={linkCls}
              data-slot="fleet-alerts-link"
            >
              Alerts
            </Link>
            <Link
              to="/c/$slug/agents/budget-incidents"
              params={{ slug: company.slug }}
              search={keepFocus}
              className={linkCls}
              data-slot="fleet-incidents-link"
            >
              Budget incidents
            </Link>
          </span>
        }
      />
      <div className="flex min-w-0 flex-col px-6 pt-3 max-md:px-4">
        {q.isPending ? (
          <Loading label="Loading the fleet" rows={4} />
        ) : q.isError ? (
          <LoadError what="The fleet" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={ServerIcon}
            title="No digital employees"
            description="The fleet lists each digital employee once it has a seat."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table data-slot="fleet-table">
              <TableCaption>Each digital employee's bridge, as it last reported.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Digital employee</TableHead>
                  <TableHead scope="col">Host</TableHead>
                  <TableHead scope="col">Bridge</TableHead>
                  <TableHead scope="col">Telemetry</TableHead>
                  <TableHead scope="col">Last heartbeat</TableHead>
                  <TableHead scope="col">Last event</TableHead>
                  <TableHead scope="col">Alerts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <FleetLine key={r.de_membership_id} row={r} company={company} limit={limit} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function FleetLine({
  row: r,
  company,
  limit,
}: {
  row: Row;
  company: CompanyAccess;
  limit: number;
}) {
  const silent = isSilent(r, limit);
  return (
    <TableRow data-de={r.de_membership_id} data-silent={silent ? "" : undefined}>
      <TableHead scope="row" className="font-medium whitespace-normal">
        <span className="flex flex-col gap-0.5">
          <Link
            to="/c/$slug/agents/de/$id"
            params={{ slug: company.slug, id: r.de_membership_id }}
            search={keepFocus}
            className={linkCls}
          >
            {r.de_name}
          </Link>
          <span className="text-xs font-normal text-muted-foreground">
            {r.seat_title ?? "No seat"}
          </span>
        </span>
      </TableHead>
      <TableCell data-slot="fleet-host">
        {r.host ?? (r.source ? <span className="text-muted-foreground">{r.source}</span> : "—")}
        {r.de_keys.length ? (
          <span className="block text-xs text-muted-foreground">Serves {r.de_keys.join(", ")}</span>
        ) : null}
      </TableCell>
      <TableCell className="tabular-nums" data-slot="fleet-version">
        {r.bridge_version ? `v${r.bridge_version}` : "—"}
        {r.uptime_s !== null ? (
          <span className="block text-xs text-muted-foreground">up {uptime(r.uptime_s)}</span>
        ) : null}
      </TableCell>
      <TableCell data-slot="fleet-telemetry">
        {r.telemetry_on === true ? (
          <Badge variant="success">On</Badge>
        ) : r.telemetry_on === false ? (
          <Badge variant="neutral">Off</Badge>
        ) : r.last_event_at ? (
          <Badge variant="info">On (no heartbeat)</Badge>
        ) : (
          <Badge variant="neutral">Never heard</Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        {r.last_heartbeat_at ? formatDateTime(r.last_heartbeat_at) : "—"}
      </TableCell>
      <TableCell className="tabular-nums">
        {r.last_event_at ? (
          <span className="flex flex-col">
            <span className={silent ? "text-danger-text" : "text-muted-foreground"}>
              {formatDateTime(r.last_event_at)}
              {silent ? " · Silent" : ""}
            </span>
            {r.last_event_type ? (
              <span className="font-mono text-xs text-muted-foreground">{r.last_event_type}</span>
            ) : null}
          </span>
        ) : (
          "—"
        )}
        <span className="block text-xs text-muted-foreground">
          {r.live_runs} run{r.live_runs === 1 ? "" : "s"} to do
        </span>
      </TableCell>
      <TableCell data-slot="fleet-alerts">
        {r.open_alerts ? (
          <span className="flex flex-col gap-1">
            <Badge variant={r.critical ? "danger" : "warning"}>{r.open_alerts} open</Badge>
            <span className="text-xs text-muted-foreground">
              {r.open_alert_kinds.map((k) => ALERT_KIND_LABELS[k]).join(", ")}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">None</span>
        )}
      </TableCell>
    </TableRow>
  );
}
